// Global Bearer-token injection for HTTP paths that DON'T go through the two
// axios instances (which have their own interceptors): raw `axios.*` calls and
// raw `fetch()` calls. This is what makes it safe to enable the backend gate
// (utils.middleware.JWTAuthMiddleware / JWT_AUTH_ENFORCED) — with it, every
// same-origin API request carries the token.
//
// Safety rules:
//   - Scoped to our own origin / API origin, so the token is NEVER sent to a
//     third-party host (careers API, S3, maps, etc.).
//   - Never overrides an Authorization header the caller already set.
//   - On any error / when no token, the ORIGINAL call runs unchanged.
//
// Import once, as a side effect, at the very top of src/index.js.
import axios from 'axios';
import { BASE_API_URL } from './api';
import { handleUnauthorized, isPublicAuthRequest, readAuthData } from './utils/authStorage';

const readToken = () => {
  try {
    const data = readAuthData();
    return data?.admin?.Token || data?.token || null;
  } catch (e) {
    return null;
  }
};

// True for relative URLs (the app's own origin) and absolute URLs that target
// our API origin. False for third-party hosts.
const isSameOriginOrApi = (url) => {
  if (!url) return false;
  if (url.startsWith('/')) return true;
  try {
    const target = new URL(url, window.location.origin).origin;
    const apiOrigin = new URL(BASE_API_URL, window.location.origin).origin;
    return target === window.location.origin || target === apiOrigin;
  } catch (e) {
    return false;
  }
};

// 1) Raw `axios.get/post(...)` on the default export (its own instances already
//    attach the token via their interceptors).
axios.interceptors.request.use((config) => {
  const token = readToken();
  if (token && isSameOriginOrApi(config.url) && !isPublicAuthRequest(config.url)) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) handleUnauthorized(error?.config?.url);
    return Promise.reject(error);
  }
);

// 2) Raw `fetch(...)`. Wrap once; the default path calls the original fetch with
//    the caller's exact arguments — we only build a new init when we actually
//    inject, so a bug here can't silently mangle unrelated requests.
const originalFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : (input && input.url);
  let response;
  try {
    const token = readToken();
    if (token && isSameOriginOrApi(url) && !isPublicAuthRequest(url)) {
      const existing = (init && init.headers) || (typeof input !== 'string' && input && input.headers) || {};
      const headers = new Headers(existing);
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
        response = await originalFetch(input, { ...(init || {}), headers });
      }
    }
  } catch (e) {
    /* fall through to the unmodified fetch below */
  }
  if (!response) response = await originalFetch(input, init);
  if (response.status === 401) handleUnauthorized(url);
  return response;
};
